import * as React from 'react'

import {
  PageMeta,
  HowToUse,
  BehindScenes,
  StepList,
  KVTable,
  Callout,
  Flow,
  Tag,
} from '@/components/docs/primitives'
import { H3, P } from '@/components/docs/prose'
import { brand } from '@/brand/brand'

export function DocCreateAd() {
  return (
    <>
      <PageMeta
        title="Create Ad"
        summary={`The 4-step wizard for producing multi-scene video ads for your products. You pick a mode, type one sentence of concept, and the AI writes a 2-to-4-scene script in your brand voice. You review, edit, and render.`}
      />

      <HowToUse>
        <P>
          The wizard collapses a full production brief into four focused decisions: what kind of
          video you want, what the AI should write, how it should be rendered, and finally —
          generate. Each step builds on the last. The running example below uses "Novera Radiance
          Serum morning routine" for avatar mode and "Novera Body Oil oil-pour on marble with
          rosehips" for product mode — swap in whichever product you are working on.
        </P>

        <H3>Step 1 — Idea</H3>
        <P>
          The first step asks three foundational questions: what mode, what style, and what is the
          core concept. These answers determine which script template the AI receives and which
          rendering engine runs each scene.
        </P>
        <StepList
          items={[
            {
              title: 'Video mode',
              body: 'Avatar mode produces a talking-head clip where an AI-generated spokesperson speaks directly to the viewer — the right choice for a testimonial about Radiance Serum or a morning-routine walkthrough for Novera Body Oil. Product mode produces cinematic or lifestyle scenes that showcase the product without a person speaking to camera — the right choice for an oil-pour on marble, a botanical still life for Shower Oil, or a pack-shot reveal of the Novera Revive Bundle. Product + Avatar combines both: the avatar holds the product and speaks while product shots intercut.',
            },
            {
              title: 'Video style',
              body: 'UGC, Cinematic, Lifestyle, or Hype Retail. This choice changes the vocabulary the AI is instructed to use when writing every scene prompt — and the words it is forbidden to use. Avatar mode defaults to UGC. Product mode defaults to Cinematic. You can override either at any time before generating. See the Getting Started page for a full description of each style.',
            },
            {
              title: 'Core concept',
              body: 'One or two sentences describing what you want the ad to show or say. Specific always beats vague. "A senior customer talks about how Novera Body Oil changed her skin texture over 30 days — warm and genuine, not a hard sell" will produce a better script than "body oil ad". If you have a product selected from your product library, its name, benefits, and price are automatically injected into the script prompt alongside your concept.',
            },
            {
              title: 'Total duration and scene count',
              body: 'Choose how long the ad runs and how many scenes to split it into. Scene durations must be exactly 5, 10, or 15 seconds. The AI allocates durations weighted by narrative importance: a quick hook gets 5 seconds, a build gets 10, a money shot gets 15. Two to four scenes works well for a Reels or TikTok format. The per-scene style picker beneath this lets you pick a different style per scene (e.g. UGC for an opening testimonial, Cinematic for a product hero) — leave it at the global style if you want every scene the same.',
            },
            {
              title: 'Same person across scenes (bridge toggle)',
              body: 'Default OFF — scenes generate independently, can show different people, run in parallel (fastest). Turn ON when you want the SAME protagonist across every scene: identity, outfit, setting locked. Bridge mode runs scenes sequentially (slower) and feeds each scene the previous scene\'s last frame as a Seedance reference. Identity may still drift slightly in illustrated-storyboard mode; for guaranteed same-face continuity, use a single 15s scene instead.',
            },
            {
              title: 'Reference images',
              body: 'Upload or pick up to nine product images from your saved context — front-on, three-quarter, and label close-up. Three consistent angles of the same Radiance bottle, for example, let the AI triangulate the real embossed label text rather than invent it. In avatar mode, the first image is the portrait that the avatar renderer animates — use a clean, well-lit face photo. Storyboard panels (when you attach a storyboard from Create Image) act as composition references — the video output stays photoreal even when the storyboard is illustrated.',
            },
          ]}
        />

        <Callout variant="tip">
          Always upload three reference images for your products: front-on, three-quarter angle,
          and a label close-up. The gold-embossed labels on amber glass are the most distinctive
          element of the packaging. Three angles give the AI enough to reconstruct the real label
          instead of hallucinating it.
        </Callout>

        <H3>Step 2 — Script</H3>
        <P>
          After you submit the Idea form, Gemini writes a full shoot-ready script and returns it
          for review before anything is rendered. You see the hook, each scene, and the CTA laid
          out as editable fields.
        </P>
        <P>
          Each scene has two distinct fields. The description is a plain-English summary of what
          the viewer will see — written for human review, not for the AI. The prompt is the actual
          generation instruction that the video renderer receives. For product scenes it follows
          the 6-step formula: Subject, Action, Environment, Camera, Style, and Constraints. For
          avatar scenes it is the spoken dialogue the spokesperson delivers.
        </P>
        <P>
          You can edit the hook, CTA, and any scene directly in the form. If you change a scene's
          duration using the 5 / 10 / 15 picker, the AI immediately rewrites that scene's prompt
          to fit the new timing — you do not need to manually adjust the wording. The per-scene
          regenerate button rewrites a single scene from scratch without touching the rest of the
          script, which is useful when one scene is off but everything else is right.
        </P>

        <Callout variant="info">
          The hook is the single most important creative decision in the script. It is the first
          visual moment or first spoken line — the thing that decides whether someone keeps
          watching. Gemini writes one, but it is worth reviewing carefully and editing to match
          your brand voice before moving on.
        </Callout>

        <H3>Step 3 — Setup</H3>
        <P>
          Setup is where you configure the output format and any additional signals to steer
          quality. These settings apply to every scene in the assembly.
        </P>
        <StepList
          items={[
            {
              title: 'Aspect ratio',
              body: '9:16 vertical for TikTok, Instagram Reels, and Shorts. 1:1 square for feed posts. 16:9 widescreen for YouTube pre-roll. The aspect ratio is applied to every scene uniformly.',
            },
            {
              title: 'Voice (avatar mode only)',
              body: 'If you leave this blank, the system auto-detects a voice from the avatar image description and picks a matching voice. You can override by picking one from the list.',
            },
            {
              title: 'Audio URL',
              body: 'Paste a URL to a pre-recorded voiceover. If left blank in avatar mode, a voice is auto-generated from the script text before the avatar renderer runs. For product scenes with no spoken dialogue, this field is ignored.',
            },
            {
              title: 'Context folders and style references',
              body: 'Context folders inject asset descriptions from your Asset Library into the generation prompt — useful for communicating environment signals or texture vocabulary (e.g. "warm bathroom, linen textures, botanical props"). Style references pull camera behaviour, lighting bias, and mood from previously analysed reference videos. Both are optional but can noticeably sharpen how closely the output matches your brand aesthetic.',
            },
            {
              title: 'Seed',
              body: 'A number that makes renders reproducible. Copy the seed from any completed job and paste it here to get similar motion and framing on the next run — useful when you are iterating on a scene you almost like.',
            },
          ]}
        />

        <H3>Step 4 — Generate</H3>
        <P>
          Review the script and settings one final time, then press Generate. The page polls the
          assembly status every 5 seconds. Each scene renders independently, so you will see them
          complete one by one rather than all at once. When all scenes are done, the clips are
          stitched into a single MP4 with clean cuts and no transitions.
        </P>

        <Callout variant="warn">
          Product scenes rendered by Seedance typically take 3 to 10 minutes each. If a single
          scene fails, you can retry it individually from the Jobs page without regenerating the
          whole assembly.
        </Callout>
      </HowToUse>

      <BehindScenes>
        <P>
          This section describes what {brand.productName} actually sends to the AI at each
          stage — the templates it is given, the bias blocks that shape the vocabulary, and the
          example prompts that result. Understanding this is the fastest way to write better
          concepts and debug unexpected output.
        </P>

        <H3>When you type your idea</H3>
        <P>
          When you submit the Idea form, Gemini receives four things stacked on top of your
          sentence, in this order:
        </P>
        <StepList
          items={[
            {
              title: 'A style bias block',
              body: 'One of the four mode-specific blocks — UGC, Cinematic, Lifestyle, or Hype Retail — that tells Gemini which vocabulary to use in every scene prompt it writes, and which words are explicitly forbidden. Changing video style changes the bias block, and that single change is responsible for most of the visible aesthetic difference between outputs.',
            },
            {
              title: 'The 6-step formula',
              body: 'An instruction to structure every product scene prompt as: Subject, Action, Environment, Camera, Style, and Constraints. Target length is 60 to 100 words per scene.',
            },
            {
              title: 'A constraint clause requirement',
              body: 'A hard rule that every scene prompt must end with the phrase: "avoid jitter, avoid bent limbs, avoid temporal flicker. Ns." — where N is the scene duration. These are the specific failure modes Seedance was trained against, and naming them explicitly reduces how often they appear.',
            },
            {
              title: 'Your concept and product context',
              body: 'Your sentence, plus the product name, benefits, ingredients, and brand voice pulled automatically from your product library entry.',
            },
          ]}
        />

        <H3>The UGC bias block</H3>
        <Callout variant="info">
          <em>
            "Lead scene prompts with phrases like 'Vertical 9:16 UGC-style footage, realistic
            handheld phone-camera feel' or 'creator-style phone-camera shot'. Use words: casual
            authentic energy, soft daylight, natural blinking, realistic skin texture, subtle head
            movement. Forbidden: cinematic, premium, shallow depth of field, golden hour, dramatic
            rim light, coloured gels, editorial, studio lighting — these push output toward stock
            AI-ad aesthetic. Camera should be handheld or fixed phone angle. End every scene with:
            'No music, no logo, no text on screen.'"
          </em>
        </Callout>

        <H3>The Cinematic bias block</H3>
        <Callout variant="info">
          <em>
            "Lead scene prompts with 'Cinematic product video, premium lighting'. Use words: slow
            dolly-in, orbit move, tracking shot, shallow depth of field, hard rim backlight, warm
            golden fill, 5600K blue tone, smooth continuous motion, luxury aesthetic. Include
            explicit lighting specificity in every scene — name the light position, colour
            temperature, and quality. Forbidden: handheld, shaky, phone-camera, casual, grainy.
            End every scene with the standard constraint clause."
          </em>
        </Callout>

        <H3>The 6-step formula in practice</H3>
        <P>
          Below is a real scene prompt for Radiance Serum, with each of the six steps
          marked in bold so you can see where the formula lands:
        </P>
        <Callout variant="info">
          <em>
            "<strong>Close-up of amber Novera Radiance Serum glass bottle</strong>{' '}
            <strong>slowly rotating 45 degrees</strong> on{' '}
            <strong>a dark obsidian countertop with scattered rosehip petals</strong>,{' '}
            <strong>single slow dolly-in</strong>,{' '}
            <strong>
              hard rim backlight with cool 5600K blue tone creating a halo edge around the glass
            </strong>{' '}
            with a thin layer of botanical mist drifting through the light, smooth continuous
            motion, shallow depth of field locked on the embossed label.{' '}
            <strong>avoid jitter, avoid bent limbs, avoid temporal flicker. 5s.</strong>"
          </em>
        </Callout>
        <P>
          The six parts are: Subject (the Radiance bottle), Action (rotating 45 degrees), Environment
          (obsidian countertop with rosehips), Camera (slow dolly-in), Style (rim backlight, blue
          tone, botanical mist), Constraints (the closing clause). When you edit a scene prompt
          manually, keep all six parts present.
        </P>

        <H3>Why reference images matter</H3>
        <P>
          When you attach reference images in Step 1, they are passed directly to the video
          renderer alongside the scene prompt. The renderer tries to preserve the exact shape,
          label text, and colour of whatever is in those images. A single hero shot of Novera Body
          Oil forces the AI to guess at the label geometry from one angle. Three consistent angles —
          front, three-quarter, and label close-up — let it triangulate the real lettering and
          packaging form. The difference in label accuracy between one image and three is
          significant and is the most reliable quality improvement available without touching any
          other setting.
        </P>

        <H3>When a scene duration changes</H3>
        <P>
          When you change the duration picker on any scene in Step 2, the AI rewrites only that
          scene's prompt for the new timing. It keeps the same narrative role and visual subject
          but adjusts the motion and camera language to fit 5, 10, or 15 seconds. The rest of the
          script is untouched.
        </P>

        <H3>When you hit Generate</H3>
        <Flow
          steps={[
            'You press Generate on Step 4',
            'An assembly is created — one render job per scene',
            'Each scene runs in the background; the UI polls every 5 seconds',
            'Product scenes render with Seedance using the scene prompt and reference images',
            'Avatar talking-head scenes render with OmniHuman using the dialogue and portrait image',
            'For avatar scenes with no audio attached, a voice is auto-generated from the script text first',
            'When all scenes complete, the clips are stitched into a single MP4 with clean cuts',
            'The final video and individual scene clips land in your Jobs page',
          ]}
        />

        <Callout variant="tip">
          Open any completed job to see its built prompt — the exact text sent to the AI, with all
          templates, bias blocks, and product context assembled. That is the single best way to
          learn what makes a strong scene prompt and to understand why an output drifted
          from your intent.
        </Callout>
      </BehindScenes>
    </>
  )
}
